import { EnvConfig, Media, MediaType, useProduct } from '@gripp/shared-logic';
import {
  colors,
  ImageGalleryImage,
  SettingsIcon,
  SlideAction,
  SlideLayout,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { FilePreviewHeaderActions, FilePreviewPanel } from '../../assets/components/filesFilePreviewPanel';
import { GalleryPanel } from '../../assets/components/filesGalleryPanel';
import { VideoPanel } from '../../assets/components/filesVideoPanel';
import { ProductDetail } from './productDetail';

type ProductFilePreviewHeaderProps = {
  media: Media;
  onEditPress: () => void;
};

export const ProductFilePreviewHeader = ({
  media,
  onEditPress,
}: ProductFilePreviewHeaderProps) => {
  const isImage = media.type === MediaType.Image;
  const isVideo = media.type === MediaType.Video;

  return (
    <View style={styles.header}>
      {!isImage && !isVideo && (
        <FilePreviewHeaderActions media={media} />
      )}
      <Pressable
        onPress={onEditPress}
        style={styles.settingsButton}
        accessibilityRole="button"
        accessibilityLabel="Edit file"
      >
        <SettingsIcon size={24} color={colors.slate[700]} />
      </Pressable>
    </View>
  );
};

type ProductFilePreviewProps = {
  media: Media;
  allImages?: Media[];
  initialIndex?: number;
};

export const ProductFilePreview = ({ media, allImages, initialIndex = 0 }: ProductFilePreviewProps) => {
  const isImage = media.type === MediaType.Image;
  const isVideo = media.type === MediaType.Video;

  const galleryImages: ImageGalleryImage[] = useMemo(() => {
    if (!isImage) return [];
    const images = allImages && allImages.length > 0 ? allImages : [media];
    return images.map((m) => ({
      id: m.id,
      url: `${EnvConfig.mediaUrl}/images/${m.id}/original.${m.extension}`,
      name: m.name,
    }));
  }, [isImage, media, allImages]);

  if (isImage) {
    return (
      <GalleryPanel
        images={galleryImages}
        initialIndex={initialIndex}
      />
    );
  }

  if (isVideo) {
    return <VideoPanel media={media} />;
  }

  return <FilePreviewPanel media={media} />;
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  settingsButton: {
    padding: 4,
  },
});

export const ProductFilePreviewPage = () => {
  const { productId = '', mediaId = '' } = useParams();
  const navigate = useNavigate();
  const { product } = useProduct(productId);

  const allFiles: Media[] = useMemo(
    () => (product?.media as Media[]) || [],
    [product?.media]
  );

  const selectedMedia = useMemo(
    () => allFiles.find((m) => m.id === mediaId) ?? null,
    [allFiles, mediaId]
  );

  const imageFiles = useMemo(
    () => allFiles.filter((m) => m.type === MediaType.Image),
    [allFiles]
  );

  const galleryImages: ImageGalleryImage[] = useMemo(
    () =>
      imageFiles.map((m) => ({
        id: m.id,
        url: `${EnvConfig.mediaUrl}/images/${m.id}/original.${m.extension}`,
        name: m.name,
      })),
    [imageFiles]
  );

  const galleryInitialIndex = useMemo(() => {
    const idx = galleryImages.findIndex((img) => img.id === mediaId);
    return idx >= 0 ? idx : 0;
  }, [galleryImages, mediaId]);

  const handleImageChange = useCallback(
    (index: number) => {
      const image = galleryImages[index];
      if (image?.id) {
        navigate(`/products/${productId}/files/${image.id}`, { replace: true });
      }
    },
    [galleryImages, productId, navigate]
  );

  const handleFilePress = useCallback(
    (media: Media, _allImages: Media[]) => {
      navigate(`/products/${productId}/files/${media.id}`);
    },
    [navigate, productId]
  );

  if (!productId || !selectedMedia) return null;

  const isImage = selectedMedia.type === MediaType.Image;
  const isVideo = selectedMedia.type === MediaType.Video;

  const previewTitle =
    selectedMedia.name && selectedMedia.name.length > 30
      ? `${selectedMedia.name.substring(0, 27)}...`
      : selectedMedia.name ?? 'File';

  const leftColumn = {
    title: 'product.list.products',
    showBackButton: true,
    backButtonTransition: SlideAction.SINGLE,
    onBackButtonPress: () => navigate('/products'),
    disableScroll: false,
    mainComponent: (
      <ProductDetailWithSlideLayout
        productId={productId}
        onFilePress={handleFilePress}
      />
    ),
  };

  const rightColumn = {
    title: previewTitle,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => navigate(`/products/${productId}`),
    disableScroll: true,
    headerComponent: (
      <ProductFilePreviewHeader
        media={selectedMedia}
        onEditPress={() =>
          navigate(`/products/${productId}/files/${mediaId}/edit`)
        }
      />
    ),
    mainComponent: isImage ? (
      <GalleryPanel
        images={galleryImages}
        initialIndex={galleryInitialIndex}
        onImageChange={handleImageChange}
      />
    ) : isVideo ? (
      <VideoPanel media={selectedMedia} />
    ) : (
      <FilePreviewPanel media={selectedMedia} />
    ),
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};

const ProductDetailWithSlideLayout = ({
  productId,
  onFilePress,
}: {
  productId: string;
  onFilePress?: (media: Media, allImages: Media[]) => void;
}) => {
  const { setColumnTitle } = useSlideLayout();
  return (
    <ProductDetail
      productId={productId}
      setColumnTitle={setColumnTitle}
      onFilePress={onFilePress}
    />
  );
};
